import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { CircleAlert } from 'lucide-react';
import { BalanceReferenceCard } from '@/components/payments/BalanceReferenceCard';
import type { BalanceReferenceDto } from '@/lib/connect-api';
import { PaymentMethodSelectionSimple } from '@/components/payments/PaymentMethodSelectionSimple';
import { useTranslation } from '@/hooks/useTranslation';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { Separator } from '@ophelos/ui/components/separator';

interface PaymentMethodTabProps {
  /** Null when no listed method is selectable yet, so no row renders checked. */
  paymentMethod: PaymentMethod | null;
  onSelectMethod: (method: PaymentMethod) => void;
  disabled?: boolean;
  error: string | null;
  /** Restrict the offered methods (e.g. card/SEPA only for scheduled payments). */
  availableMethods?: PaymentMethod[];
  /** Methods in the list that aren't yet selectable, with the reason shown on their row. */
  disabledReasons?: Partial<Record<PaymentMethod, string>>;
  /** Whether the account's shared code may be served for the payment this page is
   *  about to take — the owning page's `shouldServeSharedReference` verdict, which
   *  also decides whether the charge reuses the code. The card and the charge must
   *  answer to the same value: the code is bound to the undiscounted account total,
   *  so showing it beside one debt of several, or beside a discounted price, hands
   *  the customer a payable code for the wrong amount. Required rather than
   *  defaulted — a case-level surface must answer this, not inherit an assumption. */
  canServeSharedReference: boolean;
  /** The account's held code, read once by the owning page so this block never
   *  fetches it itself. Null when there is none to show. */
  balanceReference?: BalanceReferenceDto | null;
  excludesArrangedDebts?: boolean;
  /** A live case code for exactly this part payment's amount, when Multibanco is chosen for it.
   *  The owning page decides, on the same predicate that routes the charge to the case mint. */
  heldCaseReference?: BalanceReferenceDto | null;
}

/** The method picker and its held codes, rendered inside the amount tab under the email field. */
export function PaymentMethodTab({
  paymentMethod,
  onSelectMethod,
  disabled = false,
  error,
  availableMethods,
  disabledReasons,
  canServeSharedReference,
  balanceReference = null,
  excludesArrangedDebts = false,
  heldCaseReference = null,
}: PaymentMethodTabProps) {
  const { t } = useTranslation();

  return (
    <div className="stack gap-2 w-full">
      <div className="flex gap-4 items-center">
        <div className="text-sm font-semibold">{t('paymentMethod.selectMethod')}</div>
        <Separator className="flex-1" />
      </div>
      <PaymentMethodSelectionSimple
        selectedMethod={paymentMethod}
        onSelect={onSelectMethod}
        disabled={disabled}
        availableMethods={availableMethods}
        disabledReasons={disabledReasons}
      />

      {/* A Multibanco code the customer already holds. The page has already
          decided this payment may reuse it, method included. */}
      {canServeSharedReference && <BalanceReferenceCard reference={balanceReference} excludesArrangedDebts={excludesArrangedDebts} />}
      {heldCaseReference && <BalanceReferenceCard reference={heldCaseReference} />}

      {error && (
        <Alert variant="destructive">
          <CircleAlert />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
